import type { RouteObject } from 'react-router-dom';

import { RequireAdmin, RequireSession, SignedOutOnly } from '../components/Guard';
import { ADMIN_NAV, CREATOR_NAV } from '../nav';
import { Campaigns } from './Campaigns';
import { Privacy } from './Privacy';
import { Terms } from './Terms';
import { CampaignDetail } from './CampaignDetail';
import { Connections } from './Connections';
import { Home } from './Home';
import { Leaderboard } from './Leaderboard';
import { Profile } from './Profile';
import { Referrals } from './Referrals';
import { Submissions } from './Submissions';
import { Submit } from './Submit';
import { Support } from './Support';
import { SupportNew } from './SupportNew';
import { SupportTicket } from './SupportTicket';
import { Wallet } from './Wallet';
import { Layout } from './Layout';
import { NotFound } from './NotFound';
import { Placeholder } from './Placeholder';
import { AdminCampaignDetail, AdminCampaigns } from './admin/AdminCampaigns';
import { AdminCreators } from './admin/AdminCreators';
import { AdminInvitations } from './admin/AdminInvitations';
import { AdminLedger } from './admin/AdminLedger';
import { AdminOutbox } from './admin/AdminOutbox';
import { AdminOverview } from './admin/AdminOverview';
import { AdminPayouts } from './admin/AdminPayouts';
import { AdminReferrals } from './admin/AdminReferrals';
import { AdminReports } from './admin/AdminReports';
import { AdminReview } from './admin/AdminReview';
import { AdminSettings } from './admin/AdminSettings';
import { AdminSupport } from './admin/AdminSupport';
import { AdminSupportTicket } from './admin/AdminSupportTicket';
import { AdminUsers } from './admin/AdminUsers';
import { Login } from './auth/Login';
import { ForgotPassword, ResetPassword } from './auth/ResetPassword';
import { Signup } from './auth/Signup';
import { VerifyEmail } from './auth/VerifyEmail';

/**
 * Routes for the whole app. Every nav entry has a route, rendering a
 * placeholder until its phase lands; a later phase replaces the element in the
 * map below and nothing else moves.
 *
 * EVERY application route is behind a guard, with no exceptions — including
 * `/campaigns`, which a visitor could once read. The sidebar only exists for
 * somebody who is signed in, so an app page rendered without one is a page with
 * no way out of it; a visitor is sent to `/login?next=` instead and arrives
 * where they were going.
 */

/** Creator pages that exist now; the rest render the placeholder. */
const CREATOR_PAGES: Record<string, React.JSX.Element> = {
  '/campaigns': <Campaigns />,
  '/connections': <Connections />,
  '/profile': <Profile />,
  '/submit': <Submit />,
  '/submissions': <Submissions />,
  '/leaderboard': <Leaderboard />,
  '/wallet': <Wallet />,
  '/referrals': <Referrals />,
  '/support': <Support />,
};

/** Admin pages that exist now. */
const ADMIN_PAGES: Record<string, React.JSX.Element> = {
  '/admin/campaigns': <AdminCampaigns />,
  '/admin/outbox': <AdminOutbox />,
  '/admin/users': <AdminUsers />,
  '/admin/creators': <AdminCreators />,
  '/admin/invitations': <AdminInvitations />,
  '/admin/review': <AdminReview />,
  '/admin/payouts': <AdminPayouts />,
  '/admin/ledger': <AdminLedger />,
  '/admin/referrals': <AdminReferrals />,
  '/admin/reports': <AdminReports />,
  '/admin/support': <AdminSupport />,
  '/admin/settings': <AdminSettings />,
};

const adminChildren: RouteObject[] = [
  ...ADMIN_NAV.map((item): RouteObject =>
    item.path === '/admin'
      ? {
          index: true,
          element: (
            <RequireAdmin>
              <AdminOverview />
            </RequireAdmin>
          ),
        }
      : {
          path: item.path.slice('/admin/'.length),
          element: (
            <RequireAdmin>
              {ADMIN_PAGES[item.path] ?? (
                <Placeholder title={`Admin · ${item.label}`} phase={item.phase} />
              )}
            </RequireAdmin>
          ),
        },
  ),
  {
    path: 'campaigns/:idOrSlug',
    element: (
      <RequireAdmin>
        <AdminCampaignDetail />
      </RequireAdmin>
    ),
  },
  {
    path: 'support/:id',
    element: (
      <RequireAdmin>
        <AdminSupportTicket />
      </RequireAdmin>
    ),
  },
];

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <Layout />,
    children: [
      /* The front door is for strangers. Somebody already signed in has a home
         of their own and is sent to it. */
      {
        index: true,
        element: (
          <SignedOutOnly>
            <Home />
          </SignedOutOnly>
        ),
      },

      /* Auth pages sit outside every guard: they are how you stop being
         anonymous. Signing in twice is not a thing, so `/login` bounces a
         signed-in visitor the same way the front door does. */
      {
        path: 'login',
        element: (
          <SignedOutOnly>
            <Login />
          </SignedOutOnly>
        ),
      },
      { path: 'signup', element: <Signup /> },
      { path: 'verify-email', element: <VerifyEmail /> },
      { path: 'forgot-password', element: <ForgotPassword /> },
      { path: 'reset-password', element: <ResetPassword /> },
      /* The two documents: public, readable signed in or out. */
      { path: 'terms', element: <Terms /> },
      { path: 'privacy', element: <Privacy /> },

      ...CREATOR_NAV.map((item): RouteObject => {
        const page = CREATOR_PAGES[item.path];
        return {
          path: item.path.slice(1),
          element: (
            <RequireSession>
              {page ?? <Placeholder title={item.label} phase={item.phase} />}
            </RequireSession>
          ),
        };
      }),

      /**
       * One campaign, in full.
       *
       * NOT a child of `/campaigns`: the list is not a layout with an outlet, it
       * is a page, and nesting would keep the search box and the account strip
       * above the detail. Declared beside the nav routes rather than inside the
       * map because `CREATOR_NAV` is the SIDEBAR, and a campaign's own page is not
       * a sidebar entry — there is no menu item that leads to one campaign.
       *
       * `:slug` and not `:idOrSlug`, even though the API answers to both: the
       * address a creator sees, copies and sends to somebody is the slug, and a
       * page whose URL sometimes carries a UUID is a page nobody can read out.
       */
      {
        path: 'campaigns/:slug',
        element: (
          <RequireSession>
            <CampaignDetail />
          </RequireSession>
        ),
      },

      /* Support: the form and one ticket. `support/new` is declared before
         `support/:id` and the router ranks the static segment higher anyway,
         so "new" is never read as a ticket id. */
      {
        path: 'support/new',
        element: (
          <RequireSession>
            <SupportNew />
          </RequireSession>
        ),
      },
      {
        path: 'support/:id',
        element: (
          <RequireSession>
            <SupportTicket />
          </RequireSession>
        ),
      },

      { path: 'admin', children: adminChildren },

      { path: '*', element: <NotFound /> },
    ],
  },
];
